import { useEffect, useState } from "react";
import { FolderOpen, Info } from "lucide-react";
import { api } from "@/lib/api";
import { hilfeOeffnen } from "@/lib/useF1Help";

/**
 * BelegProjektFeld – Auswahlliste „Projekt“ an Angebot, Auftragsbestätigung und Rechnung (A-015, 17.10.2026).
 * Stil wie AuswahlFeld (natives <select>, am Handy gut bedienbar), Liste = offene Projekte des Kunden.
 *
 *   • neuer Beleg, genau ein offenes Projekt → vorausgewählt (Hinweis „vorausgewählt“),
 *   • aus dem Projekt heraus angelegt (fest) → dieses Projekt, keine Auswahl,
 *   • kein offenes Projekt → Hinweis, Anlegen bleibt möglich („ohne Projekt“),
 *   • mehrere offene Projekte und keins gewählt → Hinweis „bitte wählen“.
 *
 * Props: kundeId, value (projekt_id; null = alter Beleg ohne Angabe, "" = ohne Projekt), onChange(id),
 *        neu (neuer Beleg: Vorauswahl erlaubt), fest (Projekt nicht änderbar), testId
 */
/** Projekte des Kunden für Auswahlliste und Projekt-Schild (A-015b: einmal laden, mehrfach nutzen). */
export const useBelegProjekte = (kundeId, neuLaden = 0) => {
  const [daten, setDaten] = useState(null);
  useEffect(() => {
    if (!kundeId) { setDaten(null); return undefined; }
    let lebt = true;
    api.get(`/module-projekte/fuer-beleg/${kundeId}`)
      .then((r) => { if (lebt) setDaten(r.data || { projekte: [], offen_anzahl: 0, vorschlag: "" }); })
      .catch(() => { if (lebt) setDaten({ projekte: [], offen_anzahl: 0, vorschlag: "", fehler: true }); });
    return () => { lebt = false; };
  }, [kundeId, neuLaden]);
  return daten;
};

// daten: optional von außen (A-015b, Editor lädt einmal für Schild, Auswahl und Bauvorhaben-Zeile)
const BelegProjektFeld = ({ kundeId, value = null, onChange, neu = false, fest = false, testId = "beleg-projekt", daten: datenVonAussen }) => {
  const eigeneDaten = useBelegProjekte(datenVonAussen === undefined ? kundeId : null);
  const daten = datenVonAussen === undefined ? eigeneDaten : datenVonAussen;

  // Vorauswahl: nur bei neuem Beleg ohne Angabe und genau einem offenen Projekt
  useEffect(() => {
    if (!daten || fest || !neu || value !== null) return;
    onChange?.(daten.vorschlag || "");
  }, [daten, value, neu, fest]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!kundeId) {
    return (
      <div className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid={testId}>
        <FolderOpen className="w-4 h-4" /> Projekt: bitte zuerst einen Kunden wählen.
      </div>
    );
  }
  if (!daten) return <div className="text-xs text-muted-foreground" data-testid={testId}>Projekte werden geladen …</div>;

  const projekte = daten.projekte || [];
  const offene = projekte.filter((p) => p.offen);
  const aktuell = value ? projekte.find((p) => p.id === value) : null;
  const titel = (p) => `${p.titel}${p.status ? ` · ${p.status}` : ""}`;

  if (fest) {
    return (
      <div className="flex flex-wrap items-center gap-1.5 text-sm" data-testid={testId} data-fest="ja">
        <FolderOpen className="w-4 h-4 text-emerald-700 shrink-0" />
        <span className="font-medium">Projekt:</span>
        <span data-testid={`${testId}-fest`}>{aktuell ? titel(aktuell) : "(Projekt nicht gefunden)"}</span>
        <span className="text-[11px] text-muted-foreground">(aus dem Projekt angelegt)</span>
      </div>
    );
  }

  const vorausgewaehlt = neu && value && value === daten.vorschlag;
  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between gap-2 mb-1">
        <label className="flex items-center gap-1.5 text-sm font-medium" htmlFor={`${testId}-select`}>
          <FolderOpen className="w-4 h-4 text-emerald-700" /> Projekt
        </label>
        <button type="button" onClick={() => hilfeOeffnen("hilfe_dokumente")} className="text-[11px] text-primary hover:underline" data-testid={`${testId}-hilfe`}>Hilfe</button>
      </div>
      <select id={`${testId}-select`} value={value || ""} onChange={(e) => onChange?.(e.target.value)}
        className="w-full h-10 rounded-sm border border-input bg-background px-3 text-sm" data-testid={`${testId}-select`}>
        <option value="">— ohne Projekt —</option>
        {offene.map((p) => <option key={p.id} value={p.id}>{titel(p)}</option>)}
        {aktuell && !aktuell.offen && <option value={aktuell.id}>{titel(aktuell)} (abgeschlossen)</option>}
        {value && !aktuell && <option value={value}>(Projekt nicht gefunden)</option>}
      </select>
      {vorausgewaehlt && (
        <div className="mt-1 flex items-start gap-1.5 text-[11px] text-emerald-800 dark:text-emerald-300" data-testid={`${testId}-vorauswahl`}>
          <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" /><span>Vorausgewählt: das einzige offene Projekt dieses Kunden.</span>
        </div>
      )}
      {!value && offene.length === 0 && (
        <div className="mt-1 flex items-start gap-1.5 text-[11px] text-amber-800 dark:text-amber-300" data-testid={`${testId}-kein-projekt`}>
          <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>Dieser Kunde hat kein offenes Projekt. Sie können den Beleg ohne Projekt anlegen; dann führt die Status-Automatik nur den Kunden-Status. Projekt bei Bedarf beim Kunden anlegen und hier nachträglich wählen.</span>
        </div>
      )}
      {!value && offene.length > 1 && (
        <div className="mt-1 flex items-start gap-1.5 text-[11px] text-amber-800 dark:text-amber-300" data-testid={`${testId}-bitte-waehlen`}>
          <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>Dieser Kunde hat {offene.length} offene Projekte. Bitte wählen Sie das passende, damit der Projekt-Status mitläuft.</span>
        </div>
      )}
      {value === null && !neu && (
        <div className="mt-1 text-[11px] text-muted-foreground" data-testid={`${testId}-alt`}>Älterer Beleg ohne Projektangabe.</div>
      )}
    </div>
  );
};

export default BelegProjektFeld;
